import { useMemo } from 'react'
import { useUsage } from '~/hooks'
import {
  DYNAMIC_QUOTAS,
  QUOTA_TYPE_USER_FACING_PROPERTIES_MAP,
  QuotaUsedPercentageWarningThreshold,
} from '../../usage/constants'
import { match } from 'ts-pattern'
import { Link } from '~/elements'
import { formatValueWithUnit } from '../../usage/helpers'
import { CircleAlertIcon, TriangleAlertIcon } from 'lucide-react'
import { AlertTitle, ColoredAlert } from '@bpinternal/ui-kit-next'

type Props = {
  workspaceId: string
}

export const WorkspaceUsageCallout = ({ workspaceId }: Props) => {
  const usages = useUsage({
    workspaceId,
    quotas: DYNAMIC_QUOTAS,
  })

  const [highestUsage] = useMemo(() => {
    return Object.values(usages)
      .map(({ value, quota, ...rest }) => ({
        usage: value,
        quota,
        ...rest,
      }))
      .filter(({ percentage }) => percentage > QuotaUsedPercentageWarningThreshold)
      .sort((a, b) => b.percentage - a.percentage)
  }, [usages])

  return match(highestUsage)
    .with(undefined, () => null)
    .when(
      ({ percentage }) => percentage >= 100,
      ({ quota, type }) => (
        <ColoredAlert color="red">
          <CircleAlertIcon className="size-5" />
          <AlertTitle className="mb-0 text-sm">
            You've used <span className="font-bold">100% </span>
            of your {formatValueWithUnit(quota, type)}{' '}
            <span className="font-bold">{QUOTA_TYPE_USER_FACING_PROPERTIES_MAP[type].name}</span>. To restore
            functionality and prevent disruption, please{' '}
            <Link className="font-bold text-destructive" to="/workspaces/$workspaceId/billing" params={{ workspaceId }}>
              raise your AI spend limit
            </Link>
            .
          </AlertTitle>
        </ColoredAlert>
      )
    )
    .when(
      ({ percentage }) => percentage >= 0,
      ({ quota, type, percentage }) => (
        <ColoredAlert color="amber">
          <TriangleAlertIcon className="size-5" />
          <AlertTitle className="mb-0 text-sm">
            You have used <span className="font-bold">{percentage.toFixed(0)}% </span>
            of your {formatValueWithUnit(quota, type)} limit for{' '}
            <span className="font-bold">{QUOTA_TYPE_USER_FACING_PROPERTIES_MAP[type].name}</span>. To avoid service
            disruption, consider{' '}
            {type === 'ai_spend' || type === 'openai_spend' ? (
              <Link className="font-bold text-amber-700" to="/workspaces/$workspaceId/billing" params={{ workspaceId }}>
                raising your AI spend{' '}
              </Link>
            ) : (
              <>
                <Link
                  className="font-bold text-amber-700"
                  to="/workspaces/$workspaceId/billing"
                  params={{ workspaceId }}
                >
                  {' '}
                  upgrading your plan
                </Link>{' '}
                or{' '}
                <Link
                  className="font-bold text-amber-700"
                  to="/workspaces/$workspaceId/billing"
                  params={{ workspaceId }}
                >
                  adding an add-on
                </Link>{' '}
              </>
            )}
            to increase your limit.
          </AlertTitle>
        </ColoredAlert>
      )
    )
    .otherwise(() => null)
}
